import { useCallback, useState, useRef } from 'react';

import StageProposals from '../components/StageProposals.jsx';
import UnplacedMail from '../components/UnplacedMail.jsx';
import TaskHistory from '../components/TaskHistory.jsx';
import SuspiciousLinks from '../components/SuspiciousLinks.jsx';
import OwnTasks from '../components/OwnTasks.jsx';
import FetchMailButton from '../components/FetchMailButton.jsx';
import ScheduledList from '../components/ScheduledList.jsx';
import MailSearch from '../components/MailSearch.jsx';
import InterviewsInPlay from '../components/InterviewsInPlay.jsx';
import FollowUpsToSend from '../components/FollowUpsToSend.jsx';
import CollapsibleSection from '../components/ui/CollapsibleSection.jsx';

// T376 — one place for everything waiting on him.
//
// The operator: "vad ca e un task in dashboard dar nu pot interactiona cu el,
// poate ne-ar trebui un tab de taskuri care sa sugereze aprobarea mutarilor din
// stadii pe baza e-mailurilor venite dar nu numai."
//
// Two problems in that sentence. The dashboard's "What's next" card listed
// interviews, deadlines and tasks as plain text with no handler on the row, so
// a task was something to look at rather than something to do. And the stage
// approvals the mail raises lived on /mail, next to unlinked-message filing —
// a different kind of work at a different urgency.
//
// So: approvals first, because they change an application's history; then what
// is scheduled, with overdue at the top and every row leading somewhere.

export default function TasksPage() {
  // T415 — ScheduledList owns the events; this page only needs to know how
  // many there are, for the "nothing waiting" line.
  const [eventCount, setEventCount] = useState(null);
  const [proposalCount, setProposalCount] = useState(null);
  const scheduledRef = useRef(null);
  const interviewsRef = useRef(null);
  const followUpsRef = useRef(null);
  const [followUpCount, setFollowUpCount] = useState(null);
  // T440 — interviews in play; part of "is anything waiting on me".
  const [interviewCount, setInterviewCount] = useState(null);
  const proposalsRef = useRef(null);
  const unplacedRef = useRef(null);
  const historyRef = useRef(null);
  const suspectRef = useRef(null);
  const tasksRef = useRef(null);
  const [ownTaskCount, setOwnTaskCount] = useState(null);
  const [unplacedCount, setUnplacedCount] = useState(null);
  // T414 — the all-clear line must know about these too.
  const [suspectCount, setSuspectCount] = useState(null);

  // T387 — a fetch reloads BOTH halves of this page.
  //
  // `onDone` used to be `load`, which refetches the calendar and nothing else.
  // So a scan could finish, the toast could announce stage changes, and the
  // suggestions section would keep showing what it fetched when the page
  // opened. The half that repainted was the half he had not asked about.
  const loadAll = useCallback(async () => {
    await Promise.all([
      scheduledRef.current?.reload?.(),
      interviewsRef.current?.reload?.(),
      followUpsRef.current?.reload?.(),
      proposalsRef.current?.reload?.(),
      unplacedRef.current?.reload?.(),
      historyRef.current?.reload?.(),
      tasksRef.current?.reload?.(),
      suspectRef.current?.reload?.(),
    ]);
  }, []);

  // T414 — "nothing waiting" has to mean nothing, including the suspect
  // links. The page could list one and still print the all-clear line under
  // it, which is the badge-versus-list defect in prose form.
  const nothingAtAll =
    eventCount !== null &&
    eventCount === 0 &&
    proposalCount === 0 &&
    unplacedCount === 0 &&
    ownTaskCount === 0 &&
    suspectCount === 0 &&
    interviewCount === 0 &&
    followUpCount === 0;

  return (
    <div className="space-y-8">
      <section className="space-y-1">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h2 className="text-sm font-medium text-brand-500">Waiting on you</h2>
          {/* T384 — this page is where a fetch's results land, so it is where
              asking for one belongs too. */}
          <FetchMailButton onDone={loadAll} />
        </div>
        <p className="max-w-prose text-xs text-slate-500">
          Stage changes the mail suggests, and everything you have scheduled. Nothing here moves
          without you.
        </p>
      </section>

      {/* T387 — the section speaks for itself UNLESS the whole page is empty,
          in which case the single line at the bottom is the one voice. His
          case was the other one: an interview on screen, no suggestions, and
          nothing at all said about suggestions. */}
      {/* T440 — the interviews first. The product is steered by interviews,
          and the moments with the most at stake had the least support: on
          29 September he asked by hand for a prep document while
          `interview_prep` had been in the product for months. Above the
          approvals because an interview tomorrow outranks a stage change. */}
      <InterviewsInPlay ref={interviewsRef} onCount={setInterviewCount} />

      {/* T447 — written, not sent. 27 applications silent 14+ days and
          zero follow-ups ever sent, because the product could compose one
          and never deliver it. Below the approvals: a stage change is a
          fact arriving, this is a decision to make. */}
      <FollowUpsToSend ref={followUpsRef} onCount={setFollowUpCount} />

      <StageProposals ref={proposalsRef} onCount={setProposalCount} showEmpty={!nothingAtAll} />

      {/* T414 — the page's own design comment, from T376, says "approvals
          first, because they change an application's history; then what is
          scheduled, with overdue at the top". Scheduled had drifted to the
          bottom, below the history — so a missed interview sat under a log
          of things already done.
          T415 — and it is the same component the dashboard renders now, so
          the two cannot disagree about what is coming up. */}
      <ScheduledList ref={scheduledRef} actions onCount={setEventCount} />

      {/* T395 — the half of this page that is NOT derived from his mail. It
          sits above the mail queue because it is work he chose. */}
      <OwnTasks ref={tasksRef} onCount={setOwnTaskCount} />

      {/* T392 — mail the rules will not place on their own. It was only ever
          on the Mail page, so it never read as work waiting on him. */}
      <UnplacedMail ref={unplacedRef} onCount={setUnplacedCount} />

      {/* T406 — wrong links used to stay invisible until he opened the
          application. Above the history because it is work, below the mail
          queue because it is rarer. */}
      <SuspiciousLinks ref={suspectRef} onDone={loadAll} onCount={setSuspectCount} />

      {/* T412 — searching all mail moves here from /mail, folded.
          It is the one thing that page had which this one did not, and it
          is a lookup rather than work — so it sits below the queues and
          above the record, closed until asked for. */}
      <CollapsibleSection
        id="tasks-mail-search"
        title="Search all mail"
        hint="Every message the scan has stored, linked or not."
      >
        <MailSearch />
      </CollapsibleSection>

      {/* T394 — last, because it is a record rather than work. An undo makes
          the sections above stale, so it reloads the whole page's data. */}
      <TaskHistory ref={historyRef} onDone={loadAll} />

      {nothingAtAll && (
        <p className="text-xs text-slate-500">
          {/* T387 — it moved here in T384; the sentence still sent him to the
              dashboard for a button sitting at the top of this page. */}
          Nothing waiting. New replies arrive with{' '}
          <span className="font-mono text-slate-400">Fetch mail</span> above.
        </p>
      )}
    </div>
  );
}
